import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import ArcadeGameShell from '../ArcadeGameShell';
import { useArcadeAgentSession } from '../useArcadeAgentSession';
import { ManualPlayChrome, useManualPlay, usePlayKeyboard } from '../manualPlay';
import { GAME_BOILERPLATE } from './AGENT_BOILERPLATE';
import { ARCADE } from '../../../utils/config';
import { useManualRunRecorder } from '../useManualRunRecorder';
import {
    createC4Session,
    deleteC4Session,
    moveC4Session,
    MOVE_POINTS,
    ALIGN_2_BONUS,
    ALIGN_3_BONUS,
    WIN_BONUS,
    DRAW_BONUS,
} from './c4GmClient';
import { useTransientMessage } from '../useTransientMessage';
import { gmStartErrorMessage } from '../gmLease';
import { COLOR, GAME_ACCENT } from '../ui/tokens';

const GAME_ID = 'connect_four';
const { UPLOAD_URL } = ARCADE.connect_four;
const ACCENT = GAME_ACCENT.connect_four;
const ROWS = 6;
const COLS = 7;
const PLAYERS = ['R', 'Y'];
const ChipColor = { R: '#FF6B6B', Y: '#FFD93D' };
const HINT = '← → / A D select column · Enter / Space / ↓ drop · Esc pause';

export { MOVE_POINTS, ALIGN_2_BONUS, ALIGN_3_BONUS, WIN_BONUS, DRAW_BONUS };

/** Kept for unit/replay tests that previously imported local scoring helpers. */
export const moveIncrementalScore = (board, row, col, player) => {
    if (player !== PLAYERS[0]) return 0;
    let best = 1;
    for (const [dr, dc] of [[0, 1], [1, 0], [1, 1], [1, -1]]) {
        let length = 1;
        let r = row + dr;
        let c = col + dc;
        while (r >= 0 && r < ROWS && c >= 0 && c < COLS && board[r][c] === player) {
            length += 1;
            r += dr;
            c += dc;
        }
        r = row - dr;
        c = col - dc;
        while (r >= 0 && r < ROWS && c >= 0 && c < COLS && board[r][c] === player) {
            length += 1;
            r -= dr;
            c -= dc;
        }
        best = Math.max(best, length);
    }
    if (best >= 3) return MOVE_POINTS + ALIGN_3_BONUS;
    if (best >= 2) return MOVE_POINTS + ALIGN_2_BONUS;
    return MOVE_POINTS;
};

const emptyBoard = () => Array.from({ length: ROWS }, () => Array(COLS).fill(''));

const ConnectFour = () => {
    const play = useManualPlay();
    const sessionIdRef = useRef(null);
    const [board, setBoard] = useState(emptyBoard);
    const [currentPlayer, setCurrentPlayer] = useState(PLAYERS[0]);
    const [winner, setWinner] = useState(null);
    const [isGameOver, setIsGameOver] = useState(false);
    const [column, setColumn] = useState(3);
    const [score, setScore] = useState(0);
    const [message, setMessage] = useTransientMessage();
    const [busy, setBusy] = useState(false);

    const session = useArcadeAgentSession({ gameId: GAME_ID, uploadUrl: UPLOAD_URL });
    const recorder = useManualRunRecorder({ gameId: GAME_ID, session, copyToMoveHistory: true });

    const applyGm = useCallback((data) => {
        if (Array.isArray(data.board)) setBoard(data.board.map((r) => r.slice()));
        setCurrentPlayer(data.current_player || PLAYERS[0]);
        setWinner(data.winner ?? null);
        setIsGameOver(Boolean(data.is_game_over));
        setScore(typeof data.score === 'number' ? data.score : 0);
        setMessage(data.message || '');
        return {
            over: Boolean(data.is_game_over),
            score: typeof data.score === 'number' ? data.score : 0,
            winner: data.winner ?? null,
        };
    }, [setMessage]);

    const initFromGm = useCallback(async () => {
        if (sessionIdRef.current) {
            await deleteC4Session(sessionIdRef.current);
            sessionIdRef.current = null;
        }
        const data = await createC4Session();
        if (!data?.session_id) throw new Error('Connect Four GM missing session_id');
        sessionIdRef.current = data.session_id;
        applyGm(data);
        setColumn(3);
        recorder.reset();
    }, [applyGm, recorder]);

    const startGame = useCallback(async () => {
        try {
            setBusy(true);
            await initFromGm();
            play.startGame();
        } catch (err) {
            console.error(err);
            setMessage(gmStartErrorMessage(err));
        } finally {
            setBusy(false);
        }
    }, [initFromGm, play, setMessage]);

    const endGame = useCallback(async () => {
        const sid = sessionIdRef.current;
        sessionIdRef.current = null;
        await deleteC4Session(sid);
        play.endGame({ score });
        setBoard(emptyBoard());
        setCurrentPlayer(PLAYERS[0]);
        setWinner(null);
        setIsGameOver(false);
        setColumn(3);
        setScore(0);
        setMessage('');
    }, [play, setMessage, score]);

    useEffect(() => () => {
        const sid = sessionIdRef.current;
        sessionIdRef.current = null;
        deleteC4Session(sid);
    }, []);

    const drop = useCallback(async (col) => {
        if (play.phase !== 'playing' || isGameOver || busy) return;
        const sid = sessionIdRef.current;
        if (!sid) return;
        setBusy(true);
        try {
            const data = await moveC4Session(sid, { column: col });
            const meta = applyGm(data);
            const last = Array.isArray(data.move_history)
                ? data.move_history[data.move_history.length - 1]
                : null;
            recorder.recordTick({
                column: col,
                player: last?.player || currentPlayer,
                row: last?.row,
                score: meta.score,
            });
            if (meta.over) {
                play.markOver();
                recorder.finalizeAndUpload({ score: meta.score, extra: { winner: meta.winner } });
                const done = sessionIdRef.current;
                sessionIdRef.current = null;
                deleteC4Session(done);
            }
        } catch (err) {
            console.warn('Connect Four GM move failed', err);
            setMessage(err?.data?.message || 'Move failed');
        } finally {
            setBusy(false);
        }
    }, [applyGm, busy, currentPlayer, isGameOver, play, recorder, setMessage]);

    const onKey = useCallback((e) => {
        if (['ArrowLeft', 'a', 'A'].includes(e.key)) {
            setColumn((c) => Math.max(0, c - 1));
            e.preventDefault();
        } else if (['ArrowRight', 'd', 'D'].includes(e.key)) {
            setColumn((c) => Math.min(COLS - 1, c + 1));
            e.preventDefault();
        } else if (['Enter', ' ', 'ArrowDown', 's', 'S'].includes(e.key)) {
            drop(column);
            e.preventDefault();
        }
    }, [column, drop]);

    usePlayKeyboard({
        phase: play.phase,
        togglePause: play.togglePause,
        onKey,
        deps: [column, drop],
    });

    const available = useMemo(
        () => board[0].map((c, i) => (c ? null : i)).filter((x) => x !== null),
        [board],
    );

    return (
        <ArcadeGameShell
            title="Connect Four"
            titleAccent="Drop"
            subtitle="Drop tokens — four in a row wins"
            accentColor={ACCENT}
            boilerplate={GAME_BOILERPLATE.connect_four}
            session={session}
        >
            <ManualPlayChrome
                phase={play.phase}
                result={play.result}
                keyboardStart={false}
                onStart={startGame}
                onPause={play.togglePause}
                onEnd={endGame}
                accentColor={ACCENT}
                controlsHint={HINT}
            >
                <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                        {!isGameOver && (
                            <Chip
                                label={`Turn: ${currentPlayer}`}
                                size="small"
                                sx={{ color: ACCENT, borderColor: ACCENT }}
                                variant="outlined"
                            />
                        )}
                        {winner
                            ? <Chip label={`Winner: ${winner}`} size="small" color="success" />
                            : isGameOver
                                ? <Chip label="Draw" size="small" color="warning" />
                                : (
                                    <Chip
                                        label="In progress"
                                        size="small"
                                        variant="outlined"
                                        sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }}
                                    />
                                )}
                        <Chip
                            label={`Score: ${score}`}
                            size="small"
                            variant="outlined"
                            sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }}
                        />
                    </Stack>
                    {message && (
                        <Typography sx={{ color: COLOR.textMuted, mb: 1, fontSize: '0.85rem' }}>
                            {message}
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mb: 1 }}>
                        {Array.from({ length: COLS }).map((_, col) => (
                            <Box
                                key={col}
                                onClick={() => {
                                    if (play.phase !== 'playing' || busy) return;
                                    setColumn(col);
                                    drop(col);
                                }}
                                sx={{
                                    width: 48,
                                    height: 28,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: column === col ? ACCENT : COLOR.textSubtle,
                                    borderBottom: column === col
                                        ? `2px solid ${ACCENT}`
                                        : '2px solid transparent',
                                    cursor: available.includes(col) && play.phase === 'playing'
                                        ? 'pointer'
                                        : 'default',
                                    fontWeight: 'bold',
                                }}
                            >
                                ▼
                            </Box>
                        ))}
                    </Box>
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${COLS}, 48px)`,
                        gap: 0.5,
                        justifyContent: 'center',
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'rgba(30, 60, 120, 0.35)',
                        border: `1px solid ${COLOR.border}`,
                    }}
                    >
                        {board.map((row, r) => row.map((cell, c) => (
                            <Box
                                key={`${r}-${c}`}
                                sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '50%',
                                    bgcolor: cell ? ChipColor[cell] || '#888' : 'rgba(0,0,0,0.45)',
                                    border: c === column && play.isActive
                                        ? `2px solid ${ACCENT}`
                                        : '2px solid rgba(255,255,255,0.12)',
                                }}
                            />
                        )))}
                    </Box>
                </Box>
            </ManualPlayChrome>
        </ArcadeGameShell>
    );
};

export default ConnectFour;
